import { requireBrowserOrigin } from "@airange/shared/browser";
import {
  resolveIAiGeneratedBrowserPublicServiceBaseUrl,
  type IAiPlatformServiceKey,
} from "@airange/shared/platform-urls";

function readEnv(key: string) {
  if (typeof process !== "undefined" && process.env && typeof process.env[key] === "string") {
    return process.env[key];
  }
  return undefined;
}

export function getClientEnv(...keys: string[]) {
  for (const key of keys) {
    const value = readEnv(key);
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return undefined;
}

function currentHostname() {
  return typeof window !== "undefined" ? window.location.hostname : undefined;
}

function currentOrigin() {
  return typeof window !== "undefined" ? window.location.origin : undefined;
}

function resolveGeneratedPublicServiceUrl(service: IAiPlatformServiceKey, message: string) {
  try {
    return resolveIAiGeneratedBrowserPublicServiceBaseUrl(service, {
      hostname: currentHostname(),
      origin: currentOrigin(),
      readClientEnv: readEnv,
    });
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("Cannot resolve ")) {
      throw new Error(message);
    }
    throw error;
  }
}

function resolveAdminBackendApiBaseUrl(
  message: string,
  serviceLabel: string,
  ...keys: string[]
) {
  for (const key of keys) {
    const configured = readEnv(key);
    if (typeof configured === "string" && configured.trim()) {
      throw new Error(
        `${serviceLabel} is same-origin only in airange-admin. Remove ${key}; maintain public domains, IP+BFF paths, and service ports in deploy/service-endpoints.json plus the gateway routes instead.`,
      );
    }
  }
  return requireBrowserOrigin(message);
}

function resolveAdminMountedBackendApiBaseUrl(
  message: string,
  serviceLabel: string,
  mountPath: string,
  ...keys: string[]
) {
  const browserOrigin = resolveAdminBackendApiBaseUrl(message, serviceLabel, ...keys);
  const normalizedMountPath = mountPath.startsWith("/") ? mountPath : `/${mountPath}`;
  return `${browserOrigin.replace(/\/+$/, "")}${normalizedMountPath.replace(/\/+$/, "")}`;
}

export const SSO_API_BASE_URL = resolveGeneratedPublicServiceUrl(
  "sso-center",
  "Admin SSO base URL requires generated env or a browser host that resolves the sso service",
);

export const SSO_OIDC_SCOPE = getClientEnv("UMI_APP_SSO_OIDC_SCOPE", "VITE_SSO_OIDC_SCOPE")
  ?? "openid profile email groups offline_access";

export const ASSET_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Asset API base URL fallback is only available in browser",
  "Asset API base URL",
  "UMI_APP_ASSET_API_BASE_URL",
  "VITE_ASSET_API_BASE_URL",
);

export const EXECUTION_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Execution API base URL fallback is only available in browser",
  "Execution API base URL",
  "UMI_APP_EXECUTION_API_BASE_URL",
  "VITE_EXECUTION_API_BASE_URL",
);

export const ORCHESTRATOR_CENTER_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Orchestrator center API base URL fallback is only available in browser",
  "Orchestrator center API base URL",
  "UMI_APP_ORCHESTRATOR_CENTER_API_BASE_URL",
  "VITE_ORCHESTRATOR_CENTER_API_BASE_URL",
);

export const TASK_CENTER_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Task center API base URL fallback is only available in browser",
  "Task center API base URL",
  "UMI_APP_TASK_CENTER_API_BASE_URL",
  "VITE_TASK_CENTER_API_BASE_URL",
);

export const INTELLIGENCE_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Intelligence API base URL fallback is only available in browser",
  "Intelligence API base URL",
  "UMI_APP_INTELLIGENCE_API_BASE_URL",
  "VITE_INTELLIGENCE_API_BASE_URL",
);

export const TACTICS_TECHNIQUES_HUB_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Tactics & techniques hub API base URL fallback is only available in browser",
  "Tactics & techniques hub API base URL",
  "UMI_APP_TACTICS_TECHNIQUES_HUB_API_BASE_URL",
  "VITE_TACTICS_TECHNIQUES_HUB_API_BASE_URL",
  "TACTICS_TECHNIQUES_HUB_BASE_URL",
);

export const CAPABILITY_GRAPH_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Capability graph API base URL fallback is only available in browser",
  "Capability graph API base URL",
  "UMI_APP_CAPABILITY_GRAPH_API_BASE_URL",
  "VITE_CAPABILITY_GRAPH_API_BASE_URL",
  "CAPABILITY_GRAPH_BASE_URL",
);

export const BUSINESS_REFERENCE_CENTER_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Business reference center API base URL fallback is only available in browser",
  "Business reference center API base URL",
  "UMI_APP_BUSINESS_REFERENCE_CENTER_API_BASE_URL",
  "VITE_BUSINESS_REFERENCE_CENTER_API_BASE_URL",
  "BUSINESS_REFERENCE_CENTER_BASE_URL",
);

export const OPS_CENTER_API_BASE_URL = resolveAdminMountedBackendApiBaseUrl(
  "Ops center API base URL fallback is only available in browser",
  "Ops center API base URL",
  "/api/ops-center",
  "UMI_APP_OPS_CENTER_API_BASE_URL",
  "VITE_OPS_CENTER_API_BASE_URL",
  "OPS_CENTER_BASE_URL",
);

export const VULNERABILITY_LIBRARY_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Vulnerability library API base URL fallback is only available in browser",
  "Vulnerability library API base URL",
  "UMI_APP_VULNERABILITY_LIBRARY_API_BASE_URL",
  "VITE_VULNERABILITY_LIBRARY_API_BASE_URL",
  "VULNERABILITY_LIBRARY_BASE_URL",
);

export const TRAFFIC_LIBRARY_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Traffic library API base URL fallback is only available in browser",
  "Traffic library API base URL",
  "UMI_APP_TRAFFIC_LIBRARY_API_BASE_URL",
  "VITE_TRAFFIC_LIBRARY_API_BASE_URL",
  "TRAFFIC_LIBRARY_BASE_URL",
);

export const TOOLS_LIBRARY_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Tools library API base URL fallback is only available in browser",
  "Tools library API base URL",
  "UMI_APP_TOOLS_LIBRARY_API_BASE_URL",
  "VITE_TOOLS_LIBRARY_API_BASE_URL",
  "TOOLS_LIBRARY_BASE_URL",
);

export const KNOWLEDGE_LIBRARY_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "Knowledge library API base URL fallback is only available in browser",
  "Knowledge library API base URL",
  "UMI_APP_KNOWLEDGE_LIBRARY_API_BASE_URL",
  "VITE_KNOWLEDGE_LIBRARY_API_BASE_URL",
  "KNOWLEDGE_LIBRARY_BASE_URL",
);

export const SSO_CENTER_MANAGEMENT_API_BASE_URL = resolveAdminBackendApiBaseUrl(
  "SSO center management API base URL fallback is only available in browser",
  "SSO center management API base URL",
);

export const PORTAL_BASE_URL = resolveGeneratedPublicServiceUrl(
  "airange-portal",
  "Admin portal base URL requires generated env or a browser host that resolves the portal service",
);

export const PLATFORM_ADMIN_BASE_URL = resolveGeneratedPublicServiceUrl(
  "airange-admin",
  "Admin platform base URL requires generated env or a browser host that resolves the admin service",
);
